import { useEffect, useRef, useState } from "react";
import { useProjectActions } from "../../../core_stores/useProjectStore";
import { HttpAssistantIsWorkingException } from "../../../model/httpException";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { ImageService } from "../../../services/imageService";
import { formatNumberWithSpaces } from "../../../utils/numberFormatter";
import {
  ProjectChatTemporaryUserMessage,
  ProjectChatUserMessage,
} from "../../../view_stores/chat_messages/chatModels";
import {
  useChatElements,
  useChatMessagesActions,
} from "../../../view_stores/chat_messages/useChatMessagesStore";
import {
  ProjectStatus,
  useIsProjectProcessing,
  useProjectStatus,
} from "../../../view_stores/useProjectStatusStore";
import {
  useAttachedImage,
  useMaxPromptLength,
  usePrompt,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import {
  useCanHandleTechnicalError,
  useTechnicalErrorHandlerActions,
} from "../../../view_stores/useTechnicalErrorHandlerStore";
import { AttachedImageIcon } from "../../common_components/AttachedImageIcon";
import { AttachmentsBtn } from "../../common_components/AttachmentsBtn";

export function ProjectChatTextField() {
  const maxPromptLength = useMaxPromptLength();
  const prompt = usePrompt();
  const attachedImage = useAttachedImage();
  const chatElements = useChatElements();
  const projectStatus = useProjectStatus();
  const canHandleTechnicalError = useCanHandleTechnicalError();

  const {
    setPrompt,
    setAttachedImage,
    isLengthExceeded: isPromptLengthExceeded,
    loadFromLocalStorage: loadPromptFromLocalStorage,
    clear: clearPromptInput,
  } = usePromptInputActions();
  const { addTemporaryMessage, removeTemporaryMessages } =
    useChatMessagesActions();
  const { sendMessage } = useProjectActions();
  const { rollback } = useTechnicalErrorHandlerActions();
  const { showSnackbar } = useSnackbar();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPromptSubmitting, setIsPromptSubmitting] = useState(false);

  const isProcessing = useIsProjectProcessing();

  useEffect(() => {
    loadPromptFromLocalStorage();
  }, []);

  useEffect(() => {
    if (isProcessing) {
      setIsPromptSubmitting(false);
    } else if (projectStatus === ProjectStatus.NOT_ENOUGH_TOKENS) {
      const latestUserMessage = [...chatElements]
        .reverse()
        .find(
          (el) =>
            el instanceof ProjectChatUserMessage ||
            el instanceof ProjectChatTemporaryUserMessage
        ) as ProjectChatUserMessage | ProjectChatTemporaryUserMessage;
      if (latestUserMessage) {
        if (latestUserMessage instanceof ProjectChatUserMessage) {
          setPrompt(latestUserMessage.value.message);
          if (latestUserMessage.value.attachments?.[0]?.content) {
            setAttachedImage(
              ImageService.base64ToFile(
                latestUserMessage.value.attachments?.[0]?.content,
                latestUserMessage.value.attachments?.[0]?.filename,
                latestUserMessage.value.attachments?.[0]?.mimeType
              )
            );
          }
        } else {
          setPrompt(latestUserMessage.content);
          const attachedFile = latestUserMessage.attachedFile;
          if (attachedFile?.content) {
            setAttachedImage(
              ImageService.base64ToFile(
                attachedFile.content,
                attachedFile.filename,
                attachedFile.mimeType
              )
            );
          }
        }
      }
    }
  }, [projectStatus]);

  const canSubmit =
    !isProcessing && prompt?.trim() && !isPromptLengthExceeded();

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setAttachedImage(file);
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/jpg",
  ];

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
          e.preventDefault();
          setAttachedImage(file);
          break;
        }
      }
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const submit = async () => {
    if (canSubmit) {
      const promptToSend = prompt!.trim();
      setIsPromptSubmitting(true);
      try {
        if (canHandleTechnicalError) {
          await rollback(promptToSend, attachedImage);
          clearPromptInput();
        } else {
          addTemporaryMessage(promptToSend, attachedImage);
          clearPromptInput();

          await sendMessage(promptToSend, attachedImage);

          setIsPromptSubmitting(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      } catch (error) {
        if (error instanceof HttpAssistantIsWorkingException) {
          removeTemporaryMessages();
          setPrompt(promptToSend);
          setAttachedImage(attachedImage);
          setIsPromptSubmitting(false);
          showSnackbar(
            "Previous action is still in progress, try again later.",
            {
              type: "error",
              duration: 5000,
            }
          );
        }
      }
    }
  };

  return (
    <div className="h-full">
      <div
        className="bg-light-surface-tertiary dark:bg-dark-surface-tertiary rounded-lg 
      px-3 py-4 h-full
    flex flex-col"
      >
        <textarea
          className="w-full h-full bg-transparent resize-none focus:outline-none 
          text-light-text dark:text-dark-text 
          placeholder-light-placeholder dark:placeholder-dark-placeholder
          pr-4 pl-1"
          value={prompt ?? ""}
          placeholder="Ask Darvin..."
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          enterKeyHint="send"
        />
        <div className="flex justify-between items-center mt-2 gap-4">
          <div className="flex items-center gap-4">
            <AttachmentsBtn
              className="text-light-placeholder dark:text-dark-placeholder 
              hover:opacity-50 transition-opacity duration-200"
              onAddImageClick={handleUploadClick}
              iconSize={34}
              dropdownPosition="top"
            />
            {attachedImage && (
              <AttachedImageIcon
                attachedImg={attachedImage}
                onRemove={handleRemoveImage}
              />
            )}
          </div>
          <SubmitBtn
            isSubmitting={isPromptSubmitting}
            isDisabled={
              isPromptLengthExceeded() ||
              isPromptSubmitting ||
              isProcessing ||
              !prompt
            }
            onClick={submit}
          />
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/jpg"
          onChange={handleImageUpload}
          className="hidden"
          ref={fileInputRef}
        />
      </div>
      {isPromptLengthExceeded() && (
        <div className="text-red-500 text-sm px-2 mt-1">
          Message is too long — max {formatNumberWithSpaces(maxPromptLength)}{" "}
          characters allowed.
        </div>
      )}
    </div>
  );
}

function SubmitBtn({
  isSubmitting,
  isDisabled,
  onClick,
}: {
  isSubmitting: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        id="project-chat-text-field-send"
        className="px-7 py-1 bg-primary-blue text-white rounded-full 
        hover:bg-blue-400 transition-colors disabled:opacity-50 
        disabled:bg-[#bbbbbb] dark:disabled:bg-[#202224]
        flex items-center justify-center gap-2"
        onClick={onClick}
        disabled={isDisabled}
      >
        {isSubmitting && (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        Send
      </button>
    </div>
  );
}
